/**
 * TotpSetupDialog - Shared TOTP setup modal
 *
 * sl-d6mh.4.3: Extracted from two-factor-section.tsx to be reusable
 * across account settings and the hard-lock overlay.
 *
 * Uses the shared useTotpSetup() hook for state management.
 */
import { ConfirmModal, Input, notify } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { Checkbox } from '@affine/component/ui/checkbox';
import { OtpCodeInput } from '@affine/core/components/affine/auth/otp-code-input';
import { useTotpSetup } from '@affine/core/hooks/use-totp-setup';
import { useCallback, useState } from 'react';

import * as styles from './totp-setup-dialog.css';

export interface TotpSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export const TotpSetupDialog = ({
  open,
  onOpenChange,
  onComplete,
}: TotpSetupDialogProps) => {
  const totp = useTotpSetup();
  const [codesSaved, setCodesSaved] = useState(false);

  const handleClose = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        totp.reset();
        setCodesSaved(false);
      }
      onOpenChange(isOpen);
    },
    [onOpenChange, totp]
  );

  const onFinish = useCallback(() => {
    handleClose(false);
    onComplete();
    notify.success({ title: 'Two-factor authentication enabled' });
  }, [handleClose, onComplete]);

  const title =
    totp.step === 'password'
      ? 'Enable Two-Factor Authentication'
      : totp.step === 'qrcode'
        ? 'Scan QR Code'
        : totp.step === 'verify'
          ? 'Verify Setup'
          : 'Save Backup Codes';

  return (
    <ConfirmModal
      open={open}
      onOpenChange={handleClose}
      title={title}
      cancelText={totp.step === 'backup-codes' ? undefined : 'Cancel'}
      onConfirm={
        totp.step === 'password'
          ? totp.enableTotp
          : totp.step === 'qrcode'
            ? totp.advanceFromQrCode
            : totp.step === 'verify'
              ? totp.verifySetup
              : onFinish
      }
      confirmText={
        totp.step === 'password'
          ? 'Continue'
          : totp.step === 'qrcode'
            ? 'Next'
            : totp.step === 'verify'
              ? 'Verify'
              : 'Done'
      }
      confirmButtonOptions={{
        loading: totp.isLoading,
        disabled: totp.step === 'backup-codes' && !codesSaved,
      }}
    >
      {totp.step === 'password' && (
        <div className={styles.setupStepContent}>
          <p>Enter your password to begin setup.</p>
          <Input
            autoFocus
            type="password"
            placeholder="Password"
            value={totp.password}
            onChange={totp.setPassword}
            onEnter={totp.enableTotp}
          />
          {totp.error && <p className={styles.errorText}>{totp.error}</p>}
        </div>
      )}

      {totp.step === 'qrcode' && (
        <div className={styles.setupStepContent}>
          <p>
            Scan this QR code with your authenticator app (Google Authenticator,
            Authy, etc.):
          </p>
          {totp.qrCodeDataUrl && (
            <img
              src={totp.qrCodeDataUrl}
              alt="TOTP QR Code"
              className={styles.qrCode}
            />
          )}
          <p className={styles.secretKeyLabel}>Or enter this key manually:</p>
          <code className={styles.secretKey}>{totp.secretKey}</code>
        </div>
      )}

      {totp.step === 'verify' && (
        <div className={styles.setupStepContent}>
          <p>Enter the 6-digit code from your authenticator app:</p>
          <OtpCodeInput
            autoFocus
            value={totp.verifyCode}
            onChange={totp.setVerifyCode}
            onComplete={code => void totp.verifySetup(code)}
            disabled={totp.isLoading}
            testId="totp-setup-code-input"
          />
          {totp.error && <p className={styles.errorText}>{totp.error}</p>}
        </div>
      )}

      {totp.step === 'backup-codes' && (
        <div className={styles.setupStepContent}>
          <p>
            Save these backup codes in a safe place. Each code can only be used
            once. They will not be shown again.
          </p>
          <div className={styles.backupCodesGrid}>
            {totp.backupCodes.map((code, i) => (
              <code key={i} className={styles.backupCode}>
                {code}
              </code>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={totp.copyBackupCodes}>
              Copy all codes
            </Button>
            <Button variant="secondary" onClick={totp.downloadBackupCodes}>
              Download as .txt
            </Button>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={e => {
              if ((e.target as HTMLElement).tagName === 'INPUT') return;
              setCodesSaved(prev => !prev);
            }}
          >
            <Checkbox
              checked={codesSaved}
              onChange={(
                _e: React.ChangeEvent<HTMLInputElement>,
                checked: boolean
              ) => setCodesSaved(checked)}
            />
            <span style={{ fontSize: 13 }}>I have saved my backup codes</span>
          </div>
        </div>
      )}
    </ConfirmModal>
  );
};
